import React, { useState, useEffect, useRef } from "react";
import ManualInput from "./ManualInput";
import Button from "@mui/material/Button";
import "./SquareGrid.css";
import { BlockMath } from "react-katex";
import MathInput from "./MathInput";
import { Parser } from "expr-eval";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import themeOptions from "../themeOptions";

const baseColor = "#75280f";
const highlightColor = "#EC9C26";

const theme = createTheme(themeOptions);

function isPrime(num: number): boolean {
  if (num <= 1) return false;
  if (num === 2) return true;

  const sqrt = Math.sqrt(num);
  for (let i = 2; i <= sqrt; i++) {
    if (num % i === 0) return false;
  }
  return true;
}

function mathSquareSpiral(n: number): [number, number] {
  const lowerRoot = Math.floor(Math.sqrt(n));
  const anchor = lowerRoot ** 2;
  const location: [number, number] = [
    Math.floor(-lowerRoot / 2),
    Math.floor(lowerRoot / 2),
  ];
  location[1] -= Math.min(n - anchor, lowerRoot);
  location[0] += Math.max(n - anchor - lowerRoot, 0);
  if (lowerRoot % 2 !== 0) {
    return [location[0] * -1, location[1] * -1];
  }
  return location;
}

const ExperimentGrid: React.FC = () => {
  const [squares, setSquares] = useState(250);
  const [index, setIndex] = useState(0);
  const [formulaText, setFormulaText] = useState("4*n^2 - 4*n + 1");
  const [katexText, setKatexText] = useState("4n^2 - 4n + 1");
  const [primeMode, setPrimeMode] = useState(true);

  const formulaRef = useRef<HTMLInputElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const wh = Math.ceil(Math.sqrt(squares));
  const origin = Math.floor((wh - 1) / 2);

  function togglePrime() {
    setPrimeMode((prev) => !prev);
  }

  function newInput(input: number) {
    try {
      // Hacky way of avoiding common bugs with this library
      Parser.evaluate(formulaText, { n: 0 });
      setSquares(input);
      setIndex(0);
    } catch {
      setIndex(-1);
    }
  }

  function updateFormula() {
    const input = formulaRef.current?.value ?? "";
    try {
      Parser.evaluate(input, { n: 0 });
      setFormulaText(input);

      let katexified = "";
      for (let i = 0; i < input.length; i++) {
        if (input[i] === "*" && input[i + 1] === "n") {
          i++;
        }
        katexified += input[i];
      }
      setKatexText(katexified);
    } catch {
      setIndex(-1);
    }
  }

  function drawSquare(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    color: string,
    text: string
  ) {
    const squareSz = (1080 / wh) * 0.95;
    const padding = (1080 / wh) * 0.05;
    const yMirrorOffset = 1080 - squareSz;
    const xCoord = x * (squareSz + padding) + padding / 2;
    const yCoord = yMirrorOffset - (y * (squareSz + padding) + padding / 2);

    const fontSize = squareSz - (squareSz * text.length) / 10;
    const bottomOffset = (text.length + 1) * (squareSz / 15);
    const leftOffset = Math.max(0, (3 - text.length) * (squareSz / 7));

    ctx.fillStyle = color;
    ctx.font = `${fontSize}px serif`;

    ctx.fillRect(xCoord, yCoord, squareSz, squareSz);
    if (text.length > 0 && squares < 15000) {
      ctx.fillStyle = "black";
      ctx.fillText(
        text,
        xCoord + leftOffset,
        yCoord + squareSz - bottomOffset,
        squareSz
      );
    }
  }

  function clearGrid() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, 1080, 1080);
    ctx.fillStyle = baseColor;
    if (squares > 20000) {
      ctx.fillRect(0, 0, 1080, 1080);
    }
    for (let i = 0; i < wh; i++) {
      for (let j = 0; j < wh; j++) {
        drawSquare(ctx, j, i, baseColor, "");
      }
    }
  }

  function iteratePrimeSquareSpiral(originIndex: number) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let iterations = Math.ceil(squares / 5);
    if (squares < 5) {
      iterations = squares;
    }
    if (squares - index <= iterations) {
      iterations = squares - index;
    }

    for (let i = 0; i < iterations; i++) {
      const [ix, iy] = mathSquareSpiral(index + i);
      const [x, y] = [ix + originIndex, iy + originIndex];
      if (isPrime(index + i)) {
        drawSquare(ctx, x, y, highlightColor, `${index + i}`);
      } else {
        drawSquare(ctx, x, y, baseColor, "");
      }
    }
    setIndex(index + iterations);
  }

  function iterateConditional(originIndex: number) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let n = 0;
    for (let i = 0; i < squares; i++) {
      const fullStatement = Parser.evaluate(formulaText, { n });
      if (fullStatement > squares) {
        setIndex(-1);
        return;
      }
      const [ix, iy] = mathSquareSpiral(fullStatement);
      const [x, y] = [ix + originIndex, iy + originIndex];
      drawSquare(ctx, x, y, highlightColor, `${fullStatement}`);
      n++;
    }
    setIndex(-1);
  }

  useEffect(() => {
    if (index === 0) {
      clearGrid();
    }
    if (index < squares && index >= 0) {
      if (primeMode) {
        iteratePrimeSquareSpiral(origin);
      } else {
        iterateConditional(origin);
      }
    } else if (index > 0) {
      setIndex(-1);
    }
  }, [index, primeMode, squares, origin]);

  return (
    <div>
      <canvas
        className="BigSquareGrid"
        ref={canvasRef}
        width={1080}
        height={1080}
      />
      <br />
      <br />
      <div className="bigGridControl">
        <ThemeProvider theme={theme}>
          <Button
            variant="outlined"
            onClick={togglePrime}
            sx={{ margin: 1 }}
          >
            {primeMode ? "Ulam Spiral" : "Custom Pattern"}
          </Button>
          {primeMode ? (
            <ManualInput
              label="Squares"
              newInput={newInput}
              start={250}
            />
          ) : (
            <MathInput
              newInput={newInput}
              formulaRef={formulaRef}
              updateFormula={updateFormula}
            />
          )}

          <div style={{ fontSize: "2em" }}>
            {primeMode ? (
              <>
                <br />
                <br />
                <br />
              </>
            ) : (
              <BlockMath math={katexText} />
            )}
          </div>
        </ThemeProvider>
      </div>
    </div>
  );
};

export default ExperimentGrid;


